import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { sectionBadge, animateCounter } from '../../utils/animationUtils';

gsap.registerPlugin(ScrollTrigger);

const OverviewServiceSection = () => {
    const containerRef = useRef(null);
    const headlineRef = useRef(null);
    const statRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states via GSAP (not CSS)
            gsap.set('.overview-badge', { opacity: 0, scale: 0 });
            gsap.set('.overview-heading-word', { opacity: 0, y: 60 });
            gsap.set('.overview-card', { clipPath: '', opacity: 1 });
            gsap.set('.ov-card-badge', { opacity: 0, scale: 0 });
            gsap.set('.ov-num', { opacity: 0, scale: 1.5 });

            sectionBadge('.overview-badge');

            // Heading stagger per word
            gsap.to('.overview-heading-word', {
                opacity: 1, y: 0, stagger: 0.1, duration: 0.7, ease: 'power3.out',
                scrollTrigger: { trigger: headlineRef.current, start: 'top 95%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Cards with directional clipPath reveals
            const cardEls = gsap.utils.toArray('.overview-card');
            const clipDirs = [
                'inset(0 0 100% 0)', 'inset(0 100% 0 0)', 'inset(100% 0 0 0)'
            ];
            const clipEnds = [
                'inset(0 0 0% 0)', 'inset(0 0% 0 0)', 'inset(0% 0 0 0)'
            ];
            cardEls.forEach((card, i) => {
                gsap.fromTo(card,
                    { clipPath: clipDirs[i], opacity: 1 },
                    {
                        clipPath: clipEnds[i],
                        duration: 0.8,
                        ease: 'power4.inOut',
                        scrollTrigger: { trigger: card, start: 'top 92%', toggleActions: 'play none none reverse', fastScrollEnd: true },
                        onComplete: () => {
                            // After reveal: animate badge and num
                            gsap.to(card.querySelector('.ov-card-badge'), {
                                opacity: 1, scale: 1, ease: 'elastic.out(1,0.5)', duration: 0.6
                            });
                            gsap.to(card.querySelector('.ov-num'), {
                                opacity: 0.4, scale: 1, ease: 'power3.out', duration: 0.5
                            });
                        }
                    }
                );

                // Hover interactions
                const shadow = i === 1 ? '10px 10px 0 rgba(255,255,255,0.1)' : '10px 10px 0 #111';
                const defaultShadow = i === 1 ? '5px 5px 0 rgba(255,255,255,0.05)' : '5px 5px 0 #111';
                card.addEventListener('mouseenter', () => {
                    gsap.to(card, { y: -6, duration: 0.2, ease: 'power2.out', boxShadow: shadow });
                    const num = card.querySelector('.ov-num');
                    if (num) gsap.to(num, { opacity: 0.8, duration: 0.2 });
                });
                card.addEventListener('mouseleave', () => {
                    gsap.to(card, { y: 0, duration: 0.5, ease: 'elastic.out(1,0.5)', boxShadow: defaultShadow });
                    const num = card.querySelector('.ov-num');
                    if (num) gsap.to(num, { opacity: 0.4, duration: 0.3 });
                });

                // Badge hover
                const badge = card.querySelector('.ov-card-badge');
                if (badge) {
                    badge.addEventListener('mouseenter', () => {
                        gsap.to(badge, { scale: 1.1, rotation: gsap.utils.random(-5, 5), duration: 0.2 });
                    });
                    badge.addEventListener('mouseleave', () => {
                        gsap.to(badge, { scale: 1, rotation: 0, duration: 0.3 });
                    });
                }
            });

            // CountUp for 30% — upgraded to expo.out via animateCounter
            if (statRef.current) {
                animateCounter(statRef.current, 30, '%', 1.8);
            }
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={containerRef} id="how-i-work" className="py-24 font-jakarta overflow-hidden bg-[#F2EFE9]">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
                    <div>
                        <div
                            className="overview-badge inline-flex items-center gap-2 px-4 py-2 bg-[#FF3366] text-white border-2 border-[#111] rounded-full mb-5"
                            style={{ boxShadow: '3px 3px 0 #111', fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}
                        >
                            <span className="w-2 h-2 rounded-full bg-white inline-block" />
                            HOW I WORK
                        </div>
                        <h2 ref={headlineRef} style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, fontSize: 'var(--text-h1)', color: 'var(--ink)' }}>
                            {['How', 'I', 'Build'].map(w => (
                                <span key={w} className="overview-heading-word inline-block mr-3">{w}</span>
                            ))}
                            <span className="overview-heading-word inline-block" style={{ color: '#FF3366', fontStyle: 'italic' }}>Great Apps</span>
                        </h2>
                    </div>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'var(--text-sm)', fontWeight: 400, lineHeight: 'var(--leading-loose)', color: 'var(--ink-muted)' }}>
                        A structured approach to every engagement.
                    </p>
                </div>

                {/* Bento Grid — 2 col top, 1 full bottom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    {/* Card 01 */}
                    <div
                        className="overview-card relative border-[3px] border-[#111] rounded-2xl p-8 overflow-hidden"
                        style={{ backgroundColor: 'white', boxShadow: '6px 6px 0 #111', minHeight: 260 }}
                    >
                        <div
                            className="ov-card-badge inline-block px-3 py-1 mb-5 border-2 border-[#111]"
                            style={{
                                backgroundColor: '#FFE600', color: '#111',
                                borderRadius: 6, boxShadow: '2px 2px 0 #111',
                                opacity: 0, scale: 0,
                                fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700,
                                letterSpacing: '0.12em', textTransform: 'uppercase',
                            }}
                        >
                            COMPONENT ARCH
                        </div>
                        <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'var(--text-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: '#111', marginBottom: 10 }}>Reusable Components</h3>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, lineHeight: 1.75, fontWeight: 400, color: '#444', marginBottom: 24 }}>
                            Proven success in boosting development efficiency through modular, reusable component design.
                        </p>
                        {/* Stat box */}
                        <div
                            style={{
                                display: 'inline-flex', flexDirection: 'column',
                                backgroundColor: '#FFE600', border: '2px solid #111',
                                boxShadow: '3px 3px 0 #111', borderRadius: 8,
                                padding: '8px 20px',
                            }}
                        >
                            <span ref={statRef} style={{ fontFamily: "'Syne', sans-serif", fontSize: '2.5rem', fontWeight: 800, color: '#111', lineHeight: 1 }}>0%</span>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.12em' }}>faster delivery</span>
                        </div>
                        {/* Outline number */}
                        <span
                            className="ov-num absolute font-space font-bold leading-none select-none pointer-events-none"
                            style={{
                                bottom: 16, right: 16,
                                fontFamily: "'Space Mono', monospace",
                                fontSize: 56, fontWeight: 700,
                                color: 'transparent', WebkitTextStroke: '1.5px rgba(0,0,0,0.2)',
                                opacity: 0,
                            }}
                        >01</span>
                    </div>

                    {/* Card 02 */}
                    <div
                        className="overview-card relative border-[3px] border-[#111] rounded-2xl p-8 overflow-hidden"
                        style={{ backgroundColor: '#111', boxShadow: '6px 6px 0 #FFE600', minHeight: 260 }}
                    >
                        <div
                            className="ov-card-badge inline-block px-3 py-1 mb-5 border-2 border-[#111] text-white"
                            style={{
                                backgroundColor: '#FF3366',
                                borderRadius: 6,
                                opacity: 0, scale: 0,
                                fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700,
                                letterSpacing: '0.12em', textTransform: 'uppercase',
                            }}
                        >
                            FULL-STACK
                        </div>
                        <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'var(--text-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff', marginBottom: 10 }}>Full-Stack Foundation</h3>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, lineHeight: 1.75, fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}>
                            React, Next.js, Laravel, Node.js — understanding the complete development lifecycle.
                        </p>
                        {/* Outline number */}
                        <span
                            className="ov-num absolute font-space font-bold leading-none select-none pointer-events-none"
                            style={{
                                bottom: 16, right: 16,
                                fontFamily: "'Space Mono', monospace",
                                fontSize: 56, fontWeight: 700,
                                color: 'transparent', WebkitTextStroke: '1.5px rgba(255,230,0,0.4)',
                                opacity: 0,
                            }}
                        >02</span>
                    </div>
                </div>

                {/* Card 03 — full width */}
                <div
                    className="overview-card relative border-[3px] border-[#111] rounded-2xl p-8 overflow-hidden"
                    style={{ backgroundColor: 'white', boxShadow: '6px 6px 0 #111', minHeight: 200 }}
                >
                    <div
                        className="ov-card-badge inline-block px-3 py-1 mb-5 border-2 border-[#111]"
                        style={{
                            backgroundColor: '#AAFF00', color: '#111',
                            borderRadius: 6, boxShadow: '2px 2px 0 #111',
                            opacity: 0, scale: 0,
                            fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700,
                            letterSpacing: '0.12em', textTransform: 'uppercase',
                        }}
                    >
                        UAT VALIDATED
                    </div>
                    <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'var(--text-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: '#111', marginBottom: 10 }}>User-Focused Approach</h3>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, lineHeight: 1.75, fontWeight: 400, color: '#444', maxWidth: '64ch' }}>
                        Technical expertise combined with User Acceptance Testing ensures products align with real user needs.
                    </p>
                    {/* Outline number */}
                    <span
                        className="ov-num absolute font-space font-bold leading-none select-none pointer-events-none"
                        style={{
                            bottom: 16, right: 16,
                            fontFamily: "'Space Mono', monospace",
                            fontSize: 56, fontWeight: 700,
                            color: 'transparent', WebkitTextStroke: '1.5px rgba(0,0,0,0.2)',
                            opacity: 0,
                        }}
                    >03</span>
                </div>
            </div>
        </section>
    );
};

export default OverviewServiceSection;